import { di } from 'react-magnetic-di';

import { isDefaultRovoChat } from '@atlassian/conversation-assistant-utils/default-agent';

import { useSelectedAgentDraftController } from '../../controllers/selected-agent-drafts/useSelectedAgentDraftController';

type SelectedAgentDraftController = ReturnType<typeof useSelectedAgentDraftController>;

/**
 * Host-owned inputs to prompt routing.
 *
 * - `isConversationAgentic` is a per-conversation fact authored upstream (the
 *   conversation's base agent identity) and passed in as a prop.
 * - `enableAgentMentions` is the effective capability for the mounted
 *   integration surface: the caller combines its integration opt-in with the
 *   `rovo_chat_mention_agents` experiment, not a per-conversation property.
 */
export type PromptRoutingInputs = {
	isConversationAgentic: boolean;
	enableAgentMentions: boolean;
};

export type PromptRouting = SelectedAgentDraftController & {
	/** An agent (specialist OR Rovo) is selected as the mention for the next turn. */
	hasActiveAgentMention: boolean;
	/** The active mention is default Rovo. */
	isRovoMentioned: boolean;
	/** A non-Rovo specialist is the mention for the next turn. */
	hasActiveSpecialistAgentMention: boolean;
	/**
	 * Input-scoped (flips as the user mentions/dismisses agents), NOT the
	 * conversation's fixed agent identity: true when a specialist owns this
	 * message, so Rovo-owned controls only apply when false.
	 */
	isSpecialistPromptInput: boolean;
	/**
	 * Capability, not per-message state: an agentic conversation where the user
	 * can switch to Rovo, so Rovo-specific controls render disabled not hidden.
	 */
	canSwitchBackToRovo: boolean;
};

/**
 * Single source of truth for prompt-input routing derivation.
 *
 * Reads the per-conversation selected-agent draft state (the "override" half)
 * and combines it with the host-owned inputs to produce the routing booleans
 * consumed across the prompt input. The caller passes the effective capability,
 * so stored state remains inert when the experiment is in control or the
 * integration has opted out.
 */
export const usePromptRouting = (
	conversationId: string,
	{ isConversationAgentic, enableAgentMentions }: PromptRoutingInputs,
): PromptRouting => {
	di(useSelectedAgentDraftController);

	const draft = useSelectedAgentDraftController(conversationId);

	const hasActiveAgentMention = enableAgentMentions && draft.activeSelectedAgentId !== undefined;
	const isRovoMentioned =
		enableAgentMentions && isDefaultRovoChat({ agentId: draft.activeSelectedAgentId });
	const hasActiveSpecialistAgentMention = hasActiveAgentMention && !isRovoMentioned;
	const isSpecialistPromptInput = isConversationAgentic && !isRovoMentioned;

	return {
		...draft,
		hasActiveAgentMention,
		isRovoMentioned,
		hasActiveSpecialistAgentMention,
		isSpecialistPromptInput,
		canSwitchBackToRovo: isConversationAgentic && enableAgentMentions,
	};
};
